import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Loader2, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { z } from 'zod';

const consultantSchema = z.object({
  name: z.string().min(2, 'Le nom doit contenir au moins 2 caractères').max(100),
  specialty: z.string().min(1, 'La spécialité est requise'),
  location: z.string().max(100).optional(),
  email: z.string().email('Email invalide').max(255).optional().or(z.literal('')),
  phone: z.string().max(20).optional(),
  hourly_rate: z.number().min(0).optional(),
  currency: z.string().default('CAD'),
  availability: z.string().default('available'),
});

const specialties = ['M&A Advisory', 'Business Valuation', 'Due Diligence', 'Tax Advisory', 'Legal', 'Financial Planning', 'Other'];
const currencies = ['CAD', 'USD', 'EUR'];
const availabilities = [
  { value: 'available', label: 'Disponible' },
  { value: 'limited', label: 'Limité' },
  { value: 'unavailable', label: 'Indisponible' },
];

interface CreateConsultantDialogProps {
  onSuccess: () => void;
}

export function CreateConsultantDialog({ onSuccess }: CreateConsultantDialogProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [expertiseInput, setExpertiseInput] = useState('');
  const { toast } = useToast();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    specialty: '',
    location: '',
    email: '',
    phone: '',
    hourly_rate: '',
    currency: 'CAD',
    availability: 'available',
    expertise: [] as string[],
  });

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const addExpertise = () => {
    const trimmed = expertiseInput.trim();
    if (trimmed && !formData.expertise.includes(trimmed) && formData.expertise.length < 10) {
      setFormData(prev => ({ ...prev, expertise: [...prev.expertise, trimmed] }));
      setExpertiseInput('');
    }
  };

  const removeExpertise = (exp: string) => {
    setFormData(prev => ({ ...prev, expertise: prev.expertise.filter(e => e !== exp) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const dataToValidate = {
      ...formData,
      hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : undefined,
    };

    const result = consultantSchema.safeParse(dataToValidate);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    if (!user) {
      toast({ title: 'Erreur', description: 'Vous devez être connecté', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, 'consultants'), {
   name: formData.name.trim(),
   specialty: formData.specialty,
   location: formData.location.trim() || null,
   email: formData.email.trim() || null,
   phone: formData.phone.trim() || null,
   hourlyRate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
   currency: formData.currency,
   availability: formData.availability,
   expertise: formData.expertise.length > 0 ? formData.expertise : [],
   userId: user.id,
   rating: 0,
   projectsCompleted: 0,
   createdAt: serverTimestamp(),
});

      toast({ title: 'Succès', description: 'Consultant créé avec succès' });
      setFormData({
        name: '', specialty: '', location: '', email: '', phone: '',
        hourly_rate: '', currency: 'CAD', availability: 'available', expertise: [],
      });
      setOpen(false);
      onSuccess();
    } catch (error) {
      console.error('Error creating consultant:', error);
      toast({ title: 'Erreur', description: 'Impossible de créer le consultant', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Ajouter un consultant
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nouveau consultant</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nom *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Nom du consultant"
              className={errors.name ? 'border-destructive' : ''}
            />
            {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="specialty">Spécialité *</Label>
            <Select value={formData.specialty} onValueChange={(v) => handleChange('specialty', v)}>
              <SelectTrigger className={errors.specialty ? 'border-destructive' : ''}>
                <SelectValue placeholder="Sélectionner une spécialité" />
              </SelectTrigger>
              <SelectContent>
                {specialties.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.specialty && <p className="text-sm text-destructive">{errors.specialty}</p>}
          </div>

          <div className="space-y-2">
            <Label>Expertises</Label>
            <div className="flex gap-2">
              <Input
                value={expertiseInput}
                onChange={(e) => setExpertiseInput(e.target.value)}
                placeholder="Ajouter une expertise"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addExpertise())}
              />
              <Button type="button" variant="outline" onClick={addExpertise}>+</Button>
            </div>
            {formData.expertise.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {formData.expertise.map((exp) => (
                  <Badge key={exp} variant="secondary" className="gap-1">
                    {exp}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeExpertise(exp)} />
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="hourly_rate">Tarif horaire</Label>
              <Input
                id="hourly_rate"
                type="number"
                min="0"
                value={formData.hourly_rate}
                onChange={(e) => handleChange('hourly_rate', e.target.value)}
                placeholder="150"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">Devise</Label>
              <Select value={formData.currency} onValueChange={(v) => handleChange('currency', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="availability">Disponibilité</Label>
            <Select value={formData.availability} onValueChange={(v) => handleChange('availability', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availabilities.map((a) => (
                  <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Localisation</Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => handleChange('location', e.target.value)}
              placeholder="Ex: Montréal, QC"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="email@exemple.com"
              className={errors.email ? 'border-destructive' : ''}
            />
            {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Téléphone</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="(514) 555-0123"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">
              Annuler
            </Button>
            <Button type="submit" disabled={loading} className="flex-1">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Créer
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
